import { type App, computed, inject, ref } from 'vue';
import axios from 'axios';
import type { Incident, Maintenance, StatusPlugin } from './types';
import { StatusInjectKey } from './symbols';

export default async (
  app: App,
  { endpoint, immediate, interval }: { endpoint: string; immediate?: boolean; interval?: number },
) => {
  const maintenances = ref<Array<Maintenance>>([]);
  const activeMaintenances = computed(() =>
    maintenances.value.filter((maintenance) => maintenance.status !== 'completed'),
  );
  const currentMaintenance = computed(() =>
    maintenances.value.find((maintenance) => maintenance.status !== 'completed'),
  );
  const loadMaintenances = async () => {
    const res = await axios.get(`${endpoint}/scheduled-maintenances.json`);
    const json = res.data as { scheduled_maintenances: Array<Maintenance> };
    maintenances.value = json.scheduled_maintenances;
  };

  const incidents = ref<Array<Incident>>([]);
  const currentIncident = computed(() =>
    incidents.value.find((incident) => incident.status !== 'resolved'),
  );
  const loadIncidents = async () => {
    const res = await axios.get(`${endpoint}/incidents/unresolved.json`);
    const json = res.data as { incidents: Array<Incident> };
    incidents.value = json.incidents;
  };

  let intervalId = -1;
  const startAutoRefresh = () => {
    if (interval === 0) return;

    intervalId = setInterval(
      async () => {
        await Promise.all([loadMaintenances(), loadIncidents()]);
      },
      interval ?? 60 * 1000,
    );
  };
  const stopAutoRefresh = () => {
    clearInterval(intervalId);
  };

  const statusPlugin = {
    maintenances,
    activeMaintenances,
    currentMaintenance,
    loadMaintenances,
    incidents,
    currentIncident,
    loadIncidents,

    startAutoRefresh,
    stopAutoRefresh,
  } as StatusPlugin;

  app.config.globalProperties.$status = statusPlugin;
  app.provide(StatusInjectKey, statusPlugin);

  if (immediate) {
    await Promise.all([statusPlugin.loadMaintenances(), statusPlugin.loadIncidents()]);
  }
};

export const useStatusPlugin = () => {
  const statusPlugin = inject(StatusInjectKey, {
    maintenances: ref([]),
    activeMaintenances: ref([]),
    currentMaintenance: ref(null),
    loadMaintenances: async () => {},
    incidents: ref([]),
    currentIncident: ref(null),
    loadIncidents: async () => {},
    startAutoRefresh: () => {},
    stopAutoRefresh: () => {},
  }) as StatusPlugin;
  return statusPlugin;
};
